import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { useFormContext } from 'react-hook-form';
import styles from './ReportDismissedSection.module.scss';
import { CYPRESS_DATA } from './constants';
import EmployeeField from './EmployeeField';
import { formatSocialSecurityNumber } from './utils/socialSecurityNumber';

type SelectOption = {
  value: string;
  label: string;
};

type Props = {
  name: string;
  label: string;
  value: string;
  displayValue?: string;
  type?: 'text' | 'date' | 'email' | 'tel';
  options?: SelectOption[];
  error?: string;
  warning?: string;
  onEditingChange?: (fieldName: string, isEditing: boolean) => void;
};

const getCaretIndexFromClick = (
  element: HTMLElement,
  text: string,
  clientX: number,
  clientY: number
): number => {
  if (!text) {
    return 0;
  }

  const documentWithCaretApis = document as Document & {
    caretPositionFromPoint?: (
      x: number,
      y: number
    ) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };

  const resolveOffsetFromPoint = (
    container: HTMLElement,
    pointNode: Node,
    pointOffset: number
  ): number | null => {
    if (!container.contains(pointNode)) {
      return null;
    }

    const range = document.createRange();
    range.selectNodeContents(container);

    try {
      range.setEnd(pointNode, pointOffset);
      return range.toString().length;
    } catch {
      return null;
    }
  };

  if (documentWithCaretApis.caretPositionFromPoint) {
    const caretPosition = documentWithCaretApis.caretPositionFromPoint(
      clientX,
      clientY
    );
    if (caretPosition) {
      const resolvedOffset = resolveOffsetFromPoint(
        element,
        caretPosition.offsetNode,
        caretPosition.offset
      );
      if (resolvedOffset != null) {
        return Math.max(0, Math.min(resolvedOffset, text.length));
      }
    }
  }

  if (documentWithCaretApis.caretRangeFromPoint) {
    const caretRange = documentWithCaretApis.caretRangeFromPoint(
      clientX,
      clientY
    );
    if (caretRange) {
      const resolvedOffset = resolveOffsetFromPoint(
        element,
        caretRange.startContainer,
        caretRange.startOffset
      );
      if (resolvedOffset != null) {
        return Math.max(0, Math.min(resolvedOffset, text.length));
      }
    }
  }

  const style = window.getComputedStyle(element);
  const paddingTop = Number.parseFloat(style.paddingTop) || 0;
  const paddingBottom = Number.parseFloat(style.paddingBottom) || 0;
  const paddingLeft = Number.parseFloat(style.paddingLeft) || 0;
  const paddingRight = Number.parseFloat(style.paddingRight) || 0;
  const contentWidth = Math.max(
    0,
    element.clientWidth - paddingLeft - paddingRight
  );
  const contentHeight = Math.max(
    0,
    element.clientHeight - paddingTop - paddingBottom
  );
  const rect = element.getBoundingClientRect();
  const clickX = Math.max(
    0,
    Math.min(clientX - rect.left - paddingLeft, contentWidth)
  );
  const clickY = Math.max(
    0,
    Math.min(clientY - rect.top - paddingTop, contentHeight)
  );

  const font =
    style.font ||
    `${style.fontStyle} ${style.fontVariant} ${style.fontWeight} ${style.fontSize}/${style.lineHeight} ${style.fontFamily}`;

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    return text.length;
  }

  context.font = font;
  const lineHeight = Number.parseFloat(style.lineHeight);
  const fontSize = Number.parseFloat(style.fontSize) || 16;
  const resolvedLineHeight = Number.isFinite(lineHeight)
    ? lineHeight
    : fontSize * 1.2;

  const lineStartIndexes: number[] = [0];
  const lineTexts: string[] = [];
  let remainingText = text;
  let consumed = 0;

  while (remainingText.length > 0) {
    let low = 1;
    let high = remainingText.length;
    let best = 1;

    while (low <= high) {
      const middle = Math.floor((low + high) / 2);
      const candidateText = remainingText.slice(0, middle);
      if (context.measureText(candidateText).width <= contentWidth) {
        best = middle;
        low = middle + 1;
      } else {
        high = middle - 1;
      }
    }

    const nextLineText = remainingText.slice(0, best);
    lineTexts.push(nextLineText);
    consumed += nextLineText.length;
    lineStartIndexes.push(consumed);
    remainingText = remainingText.slice(nextLineText.length);
  }

  if (lineTexts.length === 0) {
    return 0;
  }

  const lineIndex = Math.max(
    0,
    Math.min(Math.floor(clickY / resolvedLineHeight), lineTexts.length - 1)
  );
  const lineText = lineTexts[lineIndex];
  const lineOffset = lineStartIndexes[lineIndex];

  const lineWidth = context.measureText(lineText).width;
  if (clickX >= lineWidth) {
    return lineOffset + lineText.length;
  }

  let low = 0;
  let high = lineText.length;

  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    const width = context.measureText(lineText.slice(0, middle)).width;
    if (width < clickX) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }

  if (low <= 0) {
    return lineOffset;
  }

  const previousWidth = context.measureText(lineText.slice(0, low - 1)).width;
  const currentWidth = context.measureText(lineText.slice(0, low)).width;
  return (
    lineOffset +
    (Math.abs(clickX - previousWidth) <= Math.abs(currentWidth - clickX)
      ? low - 1
      : low)
  );
};

/**
 * Read-first table cell: the value is plain text until the user activates it, so a
 * 100 row table only ever mounts a single live input.
 */
const EditableCell = ({
  name,
  label,
  value,
  displayValue,
  type,
  options,
  error,
  warning,
  onEditingChange,
}: Props) => {
  const { setValue } = useFormContext();
  const [isEditing, setIsEditing] = useState(false);
  const [initialCaretIndex, setInitialCaretIndex] = useState<number | null>(
    null
  );
  const valueBeforeEdit = useRef(value);

  const startEditing = () => {
    if (isEditing) {
      return;
    }
    valueBeforeEdit.current = value;
    setIsEditing(true);
    onEditingChange?.(name, true);
  };

  const stopEditing = () => {
    setIsEditing(false);
    setInitialCaretIndex(null);
    onEditingChange?.(name, false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setValue(name, valueBeforeEdit.current, { shouldValidate: true });
      stopEditing();
    }
    if (event.key === 'Enter' && !options) {
      event.preventDefault();
      (event.target as HTMLElement).blur();
    }
  };

  if (isEditing) {
    return (
      <EmployeeField
        name={name}
        label={label}
        type={type}
        options={options}
        hideLabel
        error={error}
        warning={warning}
        autoFocus
        initialCaretIndex={initialCaretIndex}
        onBlur={stopEditing}
        onKeyDown={handleKeyDown}
      />
    );
  }

  const readValue =
    displayValue ??
    (name.endsWith('.socialSecurityNumber')
      ? formatSocialSecurityNumber(value)
      : value);

  const handleMouseDown = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();

    setInitialCaretIndex(
      getCaretIndexFromClick(
        event.currentTarget,
        readValue ?? '',
        event.clientX,
        event.clientY
      )
    );

    startEditing();
  };

  return (
    <div data-cy={CYPRESS_DATA.cell}>
      <button
        type="button"
        className={classNames(styles.cellButton, {
          [styles.cellButtonInvalid]: Boolean(error),
        })}
        onMouseDown={handleMouseDown}
        onClick={startEditing}
        onFocus={startEditing}
        aria-label={`${label}${readValue ? `: ${readValue}` : ' (tomt)'}`}
      >
        {readValue || <span className={styles.cellPlaceholder}>Saknas</span>}
      </button>
      <span className={styles.cellFeedback}>
        {error && (
          <span className={styles.cellError} title={error}>
            {error}
          </span>
        )}
        {!error && warning && (
          <span className={classNames(styles.cellWarning)} title={warning}>
            {warning}
          </span>
        )}
      </span>
    </div>
  );
};

export default EditableCell;
